import { useCallback, useEffect, useState } from 'react';
import { MarkersAPI } from '../api/MarkerEndpoints';
import { HostEditsAPI } from '../api/HostEditsAPI';
import { DataQualityAPI } from '../api/DataQualityAPI';
import { ImageRightsAPI } from '../api/ImageRightsAPI';
import { IssueReportAPI } from '../api/IssueReportAPI';
import { TeamPlanAPI } from '../api/TeamPlanAPI';
import { dueWithin, overdue } from '../pages/team-plan/board';

const REFRESH_MS = 60000;

const settle = async (promise, fallback) => {
  try {
    return await promise;
  } catch {
    return fallback;
  }
};

/**
 * What is waiting for someone, for the sidebar's counters and the overview: events and host
 * changes to approve, correction proposals, accounts not asked about their pictures yet, open
 * reports, team tasks due within a week. Read on load, every minute and whenever `refreshKey`
 * changes; a part that fails to load counts as unknown (null) rather than taking the rest down.
 */
export default function useTaskCounts(enabled, refreshKey) {
  const [counts, setCounts] = useState(null);

  const load = useCallback(async () => {
    const [markers, edits, proposals, rights, issues, teamTasks, teamMilestones] = await Promise.all([
      settle(MarkersAPI.getAllMarkers({ adminAccepted: false, size: 500 }), null),
      settle(HostEditsAPI.list(), null),
      settle(DataQualityAPI.list('PENDING'), null),
      settle(ImageRightsAPI.overview(), null),
      settle(IssueReportAPI.getAll(), null),
      settle(TeamPlanAPI.tasks(), null),
      settle(TeamPlanAPI.milestones(), null),
    ]);
    const pendingItems = Array.isArray(markers?.items) ? markers.items : Array.isArray(markers) ? markers : null;
    const pendingMarkers = pendingItems ? pendingItems.filter((m) => m?.adminAccepted === false).length : null;
    const accounts = Array.isArray(rights?.accounts) ? rights.accounts : null;
    const rightsToAsk = accounts ? accounts.filter((a) => a.handle && !a.agreed && a.status === 'OPEN').length : null;
    // Pictures of accounts that agreed, still waiting to be cleared one by one.
    const rightsToClear = accounts ? accounts.filter((a) => a.agreed)
      .reduce((sum, a) => sum + (a.pictures - (a.picturesCleared || 0)), 0) : null;
    setCounts({
      pendingMarkers,
      hostEdits: Array.isArray(edits) ? edits.length : null,
      approvals: pendingMarkers == null && edits == null ? null : (pendingMarkers || 0) + (Array.isArray(edits) ? edits.length : 0),
      proposals: Array.isArray(proposals) ? proposals.length : null,
      rightsToAsk,
      rightsToClear,
      // What the sidebar counts: accounts to ask first, then pictures to clear.
      rightsTask: rightsToAsk ? rightsToAsk : rightsToClear,
      rightsWaiting: accounts ? accounts.filter((a) => a.handle && a.status === 'REQUESTED').length : null,
      rightsTotals: rights?.totals ?? null,
      openIssues: Array.isArray(issues) ? issues.filter((i) => i.status !== 'done').length : null,
      // Team tasks overdue or due within seven days; the lists feed the overview's "Bald fällig".
      teamDue: Array.isArray(teamTasks) ? dueWithin(teamTasks, 7).length : null,
      teamOverdue: Array.isArray(teamTasks) ? overdue(teamTasks).length : null,
      teamTasks: Array.isArray(teamTasks) ? teamTasks : null,
      teamMilestones: Array.isArray(teamMilestones) ? teamMilestones : null,
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [enabled, load, refreshKey]);

  return counts;
}
